import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { CaseResponseOpenCurrencyEnum, type CaseResponse } from '../services/generated/Api';

export function isGeneralShopCase(c: CaseResponse): boolean {
  return c.isActive && c.openCurrency !== CaseResponseOpenCurrencyEnum.Bonus;
}

export function isBandShelfCase(c: CaseResponse): boolean {
  return c.isActive && c.openCurrency === CaseResponseOpenCurrencyEnum.Bonus;
}

export function useCasesQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.cases,
    queryFn: async () => {
      const cases = await apiClient.api.caseControllerGetCases();
      return cases.filter(isGeneralShopCase);
    },
  });
}

export function useCasesCatalogQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.casesCatalog,
    queryFn: () => apiClient.api.caseControllerGetCatalog(),
  });
}

export function useUniversalCasesQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...queryKeys.cases, 'universal'],
    queryFn: async () => {
      const cases = await apiClient.api.caseControllerGetUniversalCases();
      return cases.filter(isGeneralShopCase);
    },
  });
}

export function useBandCasesQuery() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...queryKeys.cases, 'band-shelf'],
    queryFn: async () => {
      const cases = await apiClient.api.caseControllerGetCases();
      return cases.filter(isBandShelfCase);
    },
  });
}

export function useCaseItemDrops(caseId?: string, hashName?: string) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseItemDrops(caseId ?? '', hashName ?? ''),
    queryFn: () => apiClient.api.caseControllerGetCaseItemDrops({ caseId: caseId!, hashName: hashName! }),
    enabled: !!caseId && !!hashName,
    staleTime: 60 * 1000,
  });
}

export function useCaseDetails(caseId: string, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.caseDetails(caseId),
    queryFn: () => apiClient.api.caseControllerGetCaseDetails(caseId),
    enabled: enabled && !!caseId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
